/**
 * AutoCount - Parts List Page
 * Displays the parts catalog from the API with search and pagination.
 */

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import { listParts, Part, ApiError } from "api";

const formatPrice = (value: number | null): string =>
  value === null ? "—" : `$${value.toFixed(2)}`;

const formatPartType = (value: string): string =>
  value.charAt(0) + value.slice(1).toLowerCase();

const formatCategory = (value: string): string =>
  value
    .split("_")
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(" ");

const marginText = (part: Part): string => {
  if (part.costPrice == null || part.retailPrice == null || part.retailPrice <= 0) {
    return "—";
  }
  const dollars = part.retailPrice - part.costPrice;
  const pct = Math.round((dollars / part.retailPrice) * 100);
  return `$${dollars.toFixed(2)} (${pct}%)`;
};

function PartsList(): JSX.Element {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [parts, setParts] = useState<Part[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    // Small debounce so typing in search doesn't spam the API
    const timer = setTimeout(
      () => {
        listParts({ search: searchQuery || undefined, pageSize: 100 })
          .then((response) => {
            if (cancelled) return;
            setParts(response.parts);
            setTotal(response.total);
            setLoading(false);
          })
          .catch((err: ApiError) => {
            if (cancelled) return;
            setError(err.message);
            setLoading(false);
          });
      },
      searchQuery ? 300 : 0
    );

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [searchQuery]);

  // Category options (parent category names) derived from the loaded parts,
  // so the wholesaler can group/filter their catalog by category.
  const categoryOptions = useMemo(() => {
    const names = new Set<string>();
    parts.forEach((p) => {
      const cat = p.subCategory?.category?.name;
      if (cat) names.add(cat);
    });
    return Array.from(names).sort();
  }, [parts]);

  const filteredParts = useMemo(
    () =>
      categoryFilter
        ? parts.filter((p) => p.subCategory?.category?.name === categoryFilter)
        : parts,
    [parts, categoryFilter]
  );

  const columns = useMemo(
    () => [
      { Header: "Part Number", accessor: "partNumber", width: "14%" },
      { Header: "Name", accessor: "name", width: "26%" },
      { Header: "Category", accessor: "category", width: "12%" },
      { Header: "Brand", accessor: "brand", width: "11%" },
      { Header: "Type", accessor: "type", width: "10%" },
      { Header: "Retail", accessor: "price", width: "9%" },
      { Header: "Margin", accessor: "margin", width: "10%" },
      { Header: "Status", accessor: "status", width: "8%" },
    ],
    []
  );

  const rows = useMemo(
    () =>
      filteredParts.map((part) => ({
        partNumber: (
          <MDTypography
            component={Link}
            to={`/parts/${part.id}`}
            variant="caption"
            color="info"
            fontWeight="medium"
          >
            {part.partNumber}
          </MDTypography>
        ),
        name: part.name,
        category: part.subCategory?.category?.name
          ? formatCategory(part.subCategory.category.name)
          : part.subCategory?.name || "—",
        brand: part.brand?.name || "—",
        type: formatPartType(part.partType),
        price: formatPrice(part.retailPrice),
        margin: marginText(part),
        status: (
          <MDTypography
            variant="caption"
            color={part.isActive ? "success" : "error"}
            fontWeight="medium"
          >
            {part.isActive ? "Active" : "Inactive"}
          </MDTypography>
        ),
      })),
    [filteredParts]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  Parts Catalog {total > 0 && `(${total})`}
                </MDTypography>
                <MDButton
                  variant="gradient"
                  color="dark"
                  size="small"
                  onClick={() => navigate("/parts/add")}
                >
                  <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                  &nbsp;Add Part
                </MDButton>
              </MDBox>
              <MDBox pt={3} px={3}>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <MDInput
                      label="Search by part number, OEM number, or name..."
                      fullWidth
                      value={searchQuery}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setSearchQuery(e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Autocomplete
                      options={categoryOptions}
                      value={categoryFilter}
                      getOptionLabel={(option) => formatCategory(option)}
                      onChange={(_, value) => setCategoryFilter(value)}
                      renderInput={(params) => (
                        <MDInput {...params} label="Filter by category" />
                      )}
                    />
                  </Grid>
                </Grid>
              </MDBox>
              <MDBox pt={3}>
                {error && (
                  <MDBox px={3} pb={2}>
                    <Alert severity="error">{error}</Alert>
                  </MDBox>
                )}
                {loading ? (
                  <MDBox display="flex" justifyContent="center" py={6}>
                    <CircularProgress color="info" />
                  </MDBox>
                ) : (
                  <DataTable
                    table={{ columns, rows }}
                    isSorted={true}
                    entriesPerPage={{ defaultValue: 10, entries: [5, 10, 25, 50] }}
                    showTotalEntries={true}
                    canSearch={false}
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default PartsList;
